import React from 'react';
import {
  Pencil, Type, Pentagon, Square, Circle, MoveRight, Minus, Eraser,
  Pointer
} from 'lucide-react';

const TOOLS = [
  { id: 'select', icon: Pointer, label: 'Sélection' },
  { id: 'pencil', icon: Pencil, label: 'Crayon' },
  { id: 'text', icon: Type, label: 'Texte' },
  { id: 'polygon', icon: Pentagon, label: 'Polygone' },
  { id: 'rect', icon: Square, label: 'Rectangle' },
  { id: 'ellipse', icon: Circle, label: 'Ellipse' },
  { id: 'arrow', icon: MoveRight, label: 'Flèche' },
  { id: 'line', icon: Minus, label: 'Trait' },
  { id: 'eraser', icon: Eraser, label: 'Gomme' },
];

const STROKE_WIDTHS = [
  { value: 2, label: 'Fin' },
  { value: 4, label: 'Moyen' },
  { value: 8, label: 'Large' },
];

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#3b82f6', '#8b5cf6', '#ec4899', '#ffffff', '#000000',
];

export default function Toolbar({ activeTool, onToolChange, style, onStyleChange }) {
  const [showFillPicker, setShowFillPicker] = React.useState(false);
  const [showStrokePicker, setShowStrokePicker] = React.useState(false);

  return (
    <div className="bg-gray-900 text-white px-2 py-1.5 flex items-center gap-1 overflow-x-auto shrink-0">
      {/* Tools */}
      <div className="flex items-center gap-0.5">
        {TOOLS.map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => onToolChange(id)}
            className={`p-1.5 md:p-2 rounded-lg transition-colors flex flex-col items-center min-w-[40px]
              ${activeTool === id ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
            title={label}
          >
            <Icon size={16} />
            <span className="text-[8px] md:text-[9px] mt-0.5 leading-none">{label}</span>
          </button>
        ))}
      </div>

      <div className="w-px h-8 bg-gray-600 mx-1" />

      {/* Stroke color */}
      <div className="relative">
        <button
          onClick={() => { setShowStrokePicker((v) => !v); setShowFillPicker(false); }}
          className="flex items-center gap-1 p-1.5 rounded hover:bg-gray-700"
          title="Couleur trait"
        >
          <div className="w-5 h-5 rounded border-2 border-gray-500" style={{ backgroundColor: style.stroke }} />
          <span className="text-[9px] hidden md:inline">Trait</span>
        </button>
        {showStrokePicker && (
          <ColorPicker
            value={style.stroke}
            onChange={(c) => { onStyleChange({ ...style, stroke: c }); setShowStrokePicker(false); }}
            onClose={() => setShowStrokePicker(false)}
          />
        )}
      </div>

      {/* Fill color */}
      <div className="relative">
        <button
          onClick={() => { setShowFillPicker((v) => !v); setShowStrokePicker(false); }}
          className="flex items-center gap-1 p-1.5 rounded hover:bg-gray-700"
          title="Remplissage"
        >
          <div className="w-5 h-5 rounded border-2 border-gray-500 bg-checkered" style={{ backgroundColor: style.fill || 'transparent' }} />
          <span className="text-[9px] hidden md:inline">Fond</span>
        </button>
        {showFillPicker && (
          <ColorPicker
            value={style.fill}
            onChange={(c) => { onStyleChange({ ...style, fill: c }); setShowFillPicker(false); }}
            onClose={() => setShowFillPicker(false)}
            allowTransparent
          />
        )}
      </div>

      <div className="w-px h-8 bg-gray-600 mx-1" />

      {/* Stroke width */}
      <div className="flex items-center gap-0.5">
        {STROKE_WIDTHS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => onStyleChange({ ...style, strokeWidth: value })}
            className={`px-2 py-1 rounded text-[10px] transition-colors
              ${style.strokeWidth === value ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
            title={`Épaisseur: ${label}`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ColorPicker({ value, onChange, onClose, allowTransparent }) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute bottom-full mb-2 left-0 bg-gray-800 rounded-lg shadow-xl p-2 z-50 w-auto">
        <div className="grid grid-cols-5 gap-1 mb-2">
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => onChange(c)}
              className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110
                ${value === c ? 'border-blue-400 scale-110' : 'border-gray-600'}`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        {allowTransparent && (
          <button
            onClick={() => onChange('transparent')}
            className={`w-full py-1 text-xs rounded ${value === 'transparent' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
          >
            Transparent
          </button>
        )}
        <input
          type="color"
          value={value === 'transparent' ? '#000000' : value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-7 mt-1 cursor-pointer rounded"
        />
      </div>
    </>
  );
}
